import React, { useCallback, useEffect } from 'react'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { getIsPoliciesLoading, getPolicyDetails } from '@verzekeringapp-mono/shared/src/state/selectors/policies'
import { useNavigate, useParams } from 'react-router-dom'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import usePolicies from '@verzekeringapp-mono/shared/src/hooks/use_policies'
import PolicyDetailHeader from './components/policy_detail_header'
import GreenCard from './components/green_card'
import PolicyDetails from './components/policy_details'
import PolicyConditions from './components/policy_conditions'
import showValueMeter from '@verzekeringapp-mono/shared/src/utils/show_value_meter'
import ValueMeterLink from '~/components/ValueMeterLink'
import Tabs from '~/components/Tabs'
import ClaimDamageButton from '~/components/ClaimDamageButton'
import { Helmet } from 'react-helmet-async'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import LoadingIndicator from '~/components/LoadingIndicator'
import { getCombiPolicyPdfUrl } from '@verzekeringapp-mono/shared/src/api/policies'
import { isKlrPolicy } from '@verzekeringapp-mono/shared/src/utils/policy_utils'
import { useSecureFile } from '~/hooks/use_secure_file'
import { getKlrPolicyPdfUrl } from '@verzekeringapp-mono/shared/src/utils/url_builder'
import Button from '~/components/Button'
import { getToken } from '@verzekeringapp-mono/shared/src/state/selectors/user'

const PolicyDetailScreen: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const { getPolicy } = usePolicies()

  const policy = useAppSelector(getPolicyDetails)
  const isLoading = useAppSelector(getIsPoliciesLoading)
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const [gettingPolicyUrl, setGettingPolicyUrl] = React.useState(false)
  const token = useAppSelector(getToken)
  const { openFile } = useSecureFile()

  useEffect(() => {
    if (id === undefined) {
      navigate('/verzekeringen')

      return
    }

    getPolicy(id)
  }, [id])

  const onPolicyStatementPress = useCallback(async () => {
    if (!resourceAuthorization?.relationInfo?.relationNumber || !resourceAuthorization?.relationInfo?.agentNumber || !policy) {
      return
    }

    setGettingPolicyUrl(true)

    if (!isKlrPolicy(policy)) {
      getCombiPolicyPdfUrl(resourceAuthorization?.relationInfo?.agentNumber, resourceAuthorization?.relationInfo?.relationNumber)
        .then(response => {
          window.open(response.data.url, '_blank')
        })
        .catch(() => void 0)
        .finally(() => {
          setGettingPolicyUrl(false)
        })
    } else {
      await openFile(getKlrPolicyPdfUrl(
        resourceAuthorization?.relationInfo?.agentNumber,
        resourceAuthorization?.relationInfo?.relationNumber,
        policy.policyNumber || ''
      ), {
        extension: 'pdf',
        filename: 'polisblad.pdf',
        token: token?.accessToken || ''
      })

      setGettingPolicyUrl(false)
    }
  }, [resourceAuthorization, policy, token, openFile])

  if (!policy || isLoading) {
    return (
      <ScreenWrapper hasPadding={false}>
        <Helmet>
          <title>Polis gegevens</title>
        </Helmet>

        <div className='flex flex-col items-center rounded bg-surface-page p-8'>
          <LoadingIndicator />
        </div>
      </ScreenWrapper>
    )
  }

  return (
    <ScreenWrapper hasPadding={false}>
      <Helmet>
        <title>{policy.productName}</title>
      </Helmet>

      <div className='flex flex-col rounded bg-surface-page p-8'>
        <PolicyDetailHeader policy={policy} />

        <div className='hidden md:block'>
          <GreenCard
            withDivider
            policy={policy}
          />

          <div className='my-8 border-b border-border-default' />

          <PolicyDetails policy={policy} />

          <div className='w-1/4 hidden '>
            <ClaimDamageButton policyNumber={policy.policyNumber} />
          </div>

          <div className='my-8 border-b border-border-default' />

          <p className='subtitle mb-4'>Dekkingen</p>

          <Button
            className='primary-btn w-full max-w-[500px]'
            onClick={onPolicyStatementPress}
            disabled={gettingPolicyUrl}
            isLoading={gettingPolicyUrl}
          >
            <p className='max-w-[300px] text-text-on-brand'>
              Raadpleeg het polisblad voor je gekozen dekkingen.
            </p>
          </Button>

          {!!policy.clauses?.length && policy.clauses.length > 0 && (
            <div>
              <div className='my-8 border-b border-border-default' />

              <p className='subtitle'>Clausules</p>
            </div>
          )}

          {policy.clauses?.map(clause => (
            <div key={clause.id}>
              <p>{clause.name}</p>

              <p>{clause.description}</p>
            </div>
          ))}

          <div className='my-8 border-b border-border-default' />

          <p className='subtitle'>Waardemeter</p>

          {showValueMeter(policy) && (
            <div className='mt-2 w-[240px] rounded border border-border-strong'>
              <ValueMeterLink policy={policy} />
            </div>
          )}

          <div className='my-8 border-b border-border-default' />

          <PolicyConditions policy={policy} />
        </div>
      </div>

      <div className='md:hidden'>
        <Tabs
          tabs={[
            {
              title: 'Details',
              content: (
                <div className='flex flex-col gap-4 bg-surface-page p-8'>
                  <PolicyDetails policy={policy} />

                  <ClaimDamageButton policyNumber={policy.policyNumber} />
                </div>
              ),
              slug: 'details'
            },
            {
              title: 'Dekkingen',
              content: (
                <div className='bg-surface-page p-8'>
                  <Button
                    className='primary-btn'
                    disabled={gettingPolicyUrl}
                    onClick={onPolicyStatementPress}
                    isLoading={gettingPolicyUrl}
                  >
                    <p className='max-w-[300px] text-text-on-brand'>
                      Raadpleeg het polisblad voor je gekozen dekkingen.
                    </p>
                  </Button>
                </div>
              ),
              slug: 'dekkingen'
            },
            {
              title: 'Meer',
              content: (
                <div className='flex flex-col gap-4 bg-surface-page p-8'>
                  <GreenCard policy={policy} />

                  <PolicyConditions policy={policy} />

                  <p className='subtitle'>Waardemeters</p>

                  {showValueMeter(policy) && (
                    <div className='mt-2 w-[240px] rounded border border-border-strong'>
                      <ValueMeterLink policy={policy} />
                    </div>
                  )}
                </div>
              ),
              slug: 'meer'
            }
          ]}
        />
      </div>
    </ScreenWrapper>
  )
}

export default PolicyDetailScreen
